import { useCallback, useMemo } from 'react';
import { useFocusEffect } from 'expo-router';
import { Tabs } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Platform, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/theme';
import { PostOnboardingTutorial } from '@/components/tutorial/PostOnboardingTutorial';
import { useUserStore } from '@/state/userStore';
import { useInboxConnectionsStore } from '@/state/inboxConnectionsStore';
import { useEffect, useRef, useState } from 'react';
import { getPostOnboardingTutorialState } from '@/services/tutorial';
import { traceModuleEvaluation } from '@/utils/moduleTrace';
import { selectPlanTabBadgeCount, useNotificationStore } from '@/notifications/store';
import { logger } from '@/services/logger';
import { markStartupMetric } from '@/utils/startupMetrics';
import { usePremiumUpsellStore } from '@/state/premiumUpsellStore';

traceModuleEvaluation('app/(tabs)/_layout');

const TAB_ICONS: Record<
  string,
  { active: keyof typeof Ionicons.glyphMap; inactive: keyof typeof Ionicons.glyphMap }
> = {
  index: { active: 'airplane', inactive: 'airplane-outline' },
  plan: { active: 'calendar', inactive: 'calendar-outline' }, // Renamed from wallet, using calendar icon for planning
  travel: { active: 'map', inactive: 'map-outline' }, // Combined maps + safety
  more: { active: 'ellipsis-horizontal', inactive: 'ellipsis-horizontal-outline' }, // Combined documents + profile
  // Keep old icons for backward compatibility (hidden tabs)
  wallet: { active: 'wallet', inactive: 'wallet-outline' },
  documents: { active: 'document-text', inactive: 'document-text-outline' },
  maps: { active: 'map', inactive: 'map-outline' },
  safety: { active: 'shield-checkmark', inactive: 'shield-checkmark-outline' },
  profile: { active: 'person-circle', inactive: 'person-circle-outline' },
};

export default function TabLayout() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const shouldShowTutorial = useUserStore((state) => state.shouldShowTutorial);
  const hasCompletedTutorial = useUserStore((state) => state.hasCompletedTutorial);
  const setShouldShowTutorial = useUserStore((state) => state.setShouldShowTutorial);
  const planUnreadCount = useNotificationStore(selectPlanTabBadgeCount);
  const [tutorialVisible, setTutorialVisible] = useState(false);
  /** Set when the user dismisses the overlay so a late Firestore result cannot re-open it. */
  const tutorialUserDismissedRef = useRef(false);

  useEffect(() => {
    markStartupMetric('first_tabs_render_interactive');
  }, []);

  /** Central inbox connection snapshot — avoids each tab mounting its own checkAll trio. */
  useEffect(() => {
    if (!firebaseUser?.uid) {
      useInboxConnectionsStore.getState().reset();
      return;
    }
    void useInboxConnectionsStore.getState().refreshCached();
  }, [firebaseUser?.uid]);

  useFocusEffect(
    useCallback(() => {
      if (!firebaseUser?.uid) return;
      void useInboxConnectionsStore.getState().refreshIfStale();
    }, [firebaseUser?.uid]),
  );

  useEffect(() => {
    tutorialUserDismissedRef.current = false;
  }, [firebaseUser?.uid]);

  // Show tutorial only for eligible (new) users.
  useEffect(() => {
    let cancelled = false;
    const checkStatus = async () => {
      try {
        if (firebaseUser?.uid && !hasCompletedTutorial) {
          if (!shouldShowTutorial) {
            if (!cancelled) {
              setTutorialVisible(false);
            }
            return;
          }

          // Double-check eligibility/completion from Firestore (best effort; avoids regressions on fresh installs).
          const state = await getPostOnboardingTutorialState(firebaseUser.uid);
          if (cancelled) {
            return;
          }
          if (tutorialUserDismissedRef.current) {
            setTutorialVisible(false);
            return;
          }
          if (state.eligible && !state.completed && shouldShowTutorial && !hasCompletedTutorial) {
            setTutorialVisible(true);
          } else {
            setTutorialVisible(false);
            setShouldShowTutorial(false);
          }
          return;
        }
        if (!cancelled) {
          // Ensure the modal always dismisses once tutorial is completed or user signs out.
          setTutorialVisible(false);
        }
      } catch (error) {
        logger.warn(
          '[TabLayout] Failed to check tutorial eligibility',
          undefined,
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    };
    void checkStatus();
    return () => {
      cancelled = true;
    };
  }, [firebaseUser?.uid, hasCompletedTutorial, shouldShowTutorial, setShouldShowTutorial]);

  /** Marketing upsell + tutorial both use fullscreen modals; dismiss upsell when tutorial shows. */
  useEffect(() => {
    if (!tutorialVisible) return;
    usePremiumUpsellStore.getState().close();
  }, [tutorialVisible]);

  const handleTutorialComplete = () => {
    tutorialUserDismissedRef.current = true;
    setTutorialVisible(false);
    setShouldShowTutorial(false);
  };

  // Calculate tab bar height based on safe area
  const tabBarHeight = 56 + Math.max(insets.bottom, 8);

  const sceneContainerStyle = useMemo(
    () => ({ backgroundColor: theme.colors.background }),
    [theme.colors.background],
  );

  const tabBarStyle = useMemo(
    () => ({
      backgroundColor: theme.colors.background,
      borderTopColor: theme.colors.borderMuted,
      borderTopWidth: 1,
      height: tabBarHeight,
      paddingBottom: Math.max(insets.bottom, 8),
      paddingTop: 8,
    }),
    [theme.colors.background, theme.colors.borderMuted, tabBarHeight, insets.bottom],
  );

  const tabBarLabelStyle = useMemo(
    () => ({
      fontSize: 10,
      fontWeight: '600' as const,
    }),
    [],
  );

  // IMPORTANT:
  // Avoid function-form screenOptions; it can cause infinite option update loops in some setups.
  // Use a static options object + per-screen tabBarIcon instead.
  const baseScreenOptions = useMemo(
    () => ({
      headerShown: false,
      // Keep lazy loading enabled to avoid mounting/fetching every heavy tab at once.
      // This preserves responsiveness while we keep iOS freeze protections below.
      lazy: true,
      // Avoid background preloading heavy neighbors; it can stall first interactive transitions.
      lazyPreloadDistance: 0,
      // React Navigation freeze is useful for perf, but it can desync visible
      // content from active tab state on iOS in this app's current setup.
      freezeOnBlur: Platform.OS === 'android',
      // Keep inactive screens attached on iOS to avoid stale snapshot surfaces.
      detachInactiveScreens: Platform.OS === 'android',
      sceneContainerStyle,
      tabBarActiveTintColor: theme.colors.primary,
      tabBarInactiveTintColor: theme.colors.textSecondary,
      tabBarShowLabel: true,
      tabBarStyle,
      tabBarLabelStyle,
    }),
    [
      sceneContainerStyle,
      tabBarStyle,
      tabBarLabelStyle,
      theme.colors.primary,
      theme.colors.textSecondary,
    ],
  );

  const makeTabBarIcon = useCallback(
    (routeName: string) =>
      ({ color, size, focused }: { color: string; size: number; focused: boolean }) => {
        const icons = TAB_ICONS[routeName];
        const name = icons ? (focused ? icons.active : icons.inactive) : focused ? 'ellipse' : 'ellipse-outline';
        const isPrimary = routeName === 'plan';

        if (isPrimary) {
          return (
            <View
              style={{
                width: 44,
                height: 32,
                borderRadius: 16,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: focused ? theme.colors.primary + '20' : 'transparent',
              }}
            >
              <Ionicons name={name} size={size + 2} color={theme.colors.primary} />
            </View>
          );
        }

        return <Ionicons name={name} size={size} color={focused ? theme.colors.primary : color} />;
      },
    [theme.colors.primary],
  );

  // IMPORTANT:
  // `options={{ ... }}` inline objects + `tabBarIcon={makeTabBarIcon('x')}` will create new
  // object/function identities every render, which can trigger option update loops in Tabs.
  // Memoize both icons and options to keep them referentially stable.
  const tabIcons = useMemo(
    () => ({
      index: makeTabBarIcon('index'),
      plan: makeTabBarIcon('plan'),
      wallet: makeTabBarIcon('wallet'),
      travel: makeTabBarIcon('travel'),
      more: makeTabBarIcon('more'),
      documents: makeTabBarIcon('documents'),
      maps: makeTabBarIcon('maps'),
      safety: makeTabBarIcon('safety'),
      profile: makeTabBarIcon('profile'),
    }),
    [makeTabBarIcon],
  );

  const screenOptionsByName = useMemo(
    () => ({
      index: { title: 'Trips', tabBarIcon: tabIcons.index },
      plan: {
        title: 'Plan',
        tabBarIcon: tabIcons.plan,
        tabBarBadge: planUnreadCount > 0 ? (planUnreadCount > 99 ? '99+' : planUnreadCount) : undefined,
      },
      // Freeze Wallet on blur on both platforms to avoid post-blur JS work
      // (Firestore snapshot processing, currency conversion, etc.) competing
      // with the newly focused tab's thread.
      wallet: {
        title: 'Wallet',
        tabBarIcon: tabIcons.wallet,
        // iOS can stall or snapshot stale scenes when freezing this heavy screen.
        freezeOnBlur: Platform.OS === 'android',
      },
      // Keep Travel (Mapbox-heavy) unfrozen on iOS; freezing blurred native map scenes
      // can trigger transition stalls/snapshot contention.
      travel: {
        title: 'Travel',
        tabBarIcon: tabIcons.travel,
        freezeOnBlur: Platform.OS === 'ios' ? false : undefined,
      },
      more: { title: 'More', tabBarIcon: tabIcons.more },
      documents: { title: 'Documents', href: null, tabBarIcon: tabIcons.documents },
      maps: { title: 'Maps', href: null, tabBarIcon: tabIcons.maps },
      safety: { title: 'Safety', href: null, tabBarIcon: tabIcons.safety },
      profile: { title: 'Profile', href: null, tabBarIcon: tabIcons.profile },
      consentManagement: { href: null },
    }),
    [planUnreadCount, tabIcons],
  );

  return (
    <>
      <Tabs
        screenOptions={baseScreenOptions}
      >
        <Tabs.Screen name="index" options={screenOptionsByName.index} />
        <Tabs.Screen name="plan" options={screenOptionsByName.plan} />
        <Tabs.Screen name="wallet" options={screenOptionsByName.wallet} />
        <Tabs.Screen name="travel" options={screenOptionsByName.travel} />
        <Tabs.Screen name="more" options={screenOptionsByName.more} />
        {/* Hidden tabs - still accessible via direct navigation but not shown in tab bar */}
        <Tabs.Screen 
          name="documents" 
          options={screenOptionsByName.documents} 
        />
        <Tabs.Screen 
          name="maps" 
          options={screenOptionsByName.maps} 
        />
        <Tabs.Screen 
          name="safety" 
          options={screenOptionsByName.safety} 
        />
        <Tabs.Screen 
          name="profile" 
          options={screenOptionsByName.profile} 
        />
        <Tabs.Screen 
          name="consent-management" 
          options={screenOptionsByName.consentManagement} 
        />
      </Tabs>
      <PostOnboardingTutorial
        visible={tutorialVisible}
        onComplete={handleTutorialComplete}
      />
    </>
  );
}

